import React from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useClubContext, type Club } from '@/contexts/ClubContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Hook for managing club admin functionality
 * Provides club selection, admin verification, and club data management
 */
export function useClubAdmin() {
  const context = useClubContext();
  const { user } = useAuth();
  
  if (!context) {
    throw new Error('useClubAdmin must be used within a ClubProvider');
  }

  const {
    currentClub,
    setCurrentClub,
    isClubAdmin,
    setIsClubAdmin
  } = context;


  // Query for clubs that the current user is an admin of
  const { data: adminClubs, isLoading: isLoadingAdminClubs, error: adminClubsError } = useQuery({
    queryKey: ['admin-clubs', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_club_memberships')
        .select(`
          club_id,
          role,
          status,
          clubs (
            id,
            name,
            description,
            website_url,
            phone,
            email,
            address,
            city,
            state,
            country,
            course_count,
            location,
            created_at,
            created_by
          )
        `)
        .eq('user_id', user.id)
        .in('role', ['admin', 'owner', 'coach'])
        .eq('status', 'active');

      if (error) {
        throw error;
      }

      return data?.map(item => ({
        ...item.clubs,
        admin_role: item.role
      })) || [];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Query for current club details if one is selected
  const { data: clubDetails, isLoading: isLoadingClubDetails } = useQuery({
    queryKey: ['club-details', currentClub?.id],
    queryFn: async () => {
      if (!currentClub?.id) return null;
      
      const { data, error } = await supabase
        .from('clubs')
        .select(`
          *,
          courses (
            id,
            name,
            hole_count,
            has_gps
          )
        `)
        .eq('id', currentClub.id)
        .single();
      
      if (error) throw error;
      
      return data;
    },
    enabled: !!currentClub?.id,
  });

  // Function to switch to a different club (for multi-club admins)
  const switchClub = async (club: Club) => {
    if (!user?.id) throw new Error('User not authenticated');
    
    // Verify user is admin of this club
    const { data, error } = await supabase
      .from('user_club_memberships')
      .select('role')
      .eq('club_id', club.id)
      .eq('user_id', user.id)
      .in('role', ['admin', 'owner', 'coach'])
      .eq('status', 'active')
      .single();
    
    if (error || !data) {
      throw new Error('You are not authorized to manage this club');
    }
    
    setCurrentClub(club);
    setIsClubAdmin(true);
  };

  // SECURITY FIX: Only auto-select club AFTER server verification
  // Remove the automatic localStorage-based selection
  React.useEffect(() => {
    if (!currentClub && adminClubs && adminClubs.length > 0) {
      // Check if there's a target club ID from recent signup
      const targetClubId = localStorage.getItem('targetClubId');

      let clubToSelect = adminClubs[0]; // Default to first VERIFIED admin club

      if (targetClubId) {
        // Find the target club in VERIFIED admin clubs only
        const targetClub = adminClubs.find(club => club.id === targetClubId);
        if (targetClub) {
          clubToSelect = targetClub;
          console.log('🔍 DEBUG: Selecting target club from localStorage:', targetClub.name);
        }
        // Clear the target club ID after use
        localStorage.removeItem('targetClubId');
      }

      // Only set club if it's verified through server query
      setCurrentClub(clubToSelect);
      setIsClubAdmin(true);

      console.log('✅ SECURITY: Club selected after server verification:', clubToSelect.name);
    }
  }, [adminClubs, currentClub, setCurrentClub, setIsClubAdmin]);

  // SECURITY FIX: Clear localStorage on context reset
  React.useEffect(() => {
    if (!isClubAdmin && currentClub) {
      setCurrentClub(null);
      localStorage.removeItem('currentAdminClub');
      console.log('🔒 SECURITY: Cleared unauthorized club selection');
    }
  }, [isClubAdmin, currentClub, setCurrentClub]);

  return {
    // Current club state
    currentClub,
    setCurrentClub,
    isClubAdmin,
    
    // Admin clubs
    adminClubs: adminClubs || [],
    isLoadingAdminClubs,
    hasMultipleClubs: (adminClubs?.length || 0) > 1,
    
    // Current club details
    clubDetails,
    isLoadingClubDetails,
    
    // Actions
    switchClub,
    
    // Computed values
    isLoading: isLoadingAdminClubs || isLoadingClubDetails,
    canAccessAdmin: isClubAdmin && !!currentClub && !!(adminClubs?.find(club => club.id === currentClub.id)),
  };
}